/**
 * Human-readable surface layer for AI-chat tool calls.
 *
 * Three places rendered raw internals straight into the DOM: the streaming
 * status line showed the bare tool id with no mapping at all
 * (`thinking-indicator.tsx`); the tool-call chip fell back to
 * `toolName.replace(/_/g, " ")` for any of the 953-of-998 tools missing from
 * a 45-entry hand map (`tool-result-card.tsx`); and the verification dialog
 * printed raw argument keys in monospace with `JSON.stringify` for anything
 * non-scalar (`verification-dialog.tsx`).
 *
 * The backend now attaches an authored `toolLabel` to SSE tool events, the
 * human-verification event, and persisted `ai_messages.toolCalls` rows — but
 * conversations that predate that change carry no `toolLabel`, so every
 * caller here must tolerate it being absent. This module is the one place
 * that resolves "what do we show the human" so the fallback rule is only
 * implemented once.
 */

// ── Tool name -> label ──────────────────────────────────────────────────────

/**
 * Turns a machine tool id ("content_studio_session_create" or
 * "recipientBulkDelete") into a lowercase-words sentence fragment. Never
 * returns the raw identifier and never returns a string containing an
 * underscore or hyphen.
 */
export function humanizeToolName(toolName: string | undefined | null): string {
  const trimmed = (toolName ?? '').trim()
  if (!trimmed) return 'this action'
  const spaced = trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  if (!spaced) return 'this action'
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Prefers the server-authored `toolLabel`; falls back to `humanizeToolName`
 * when it is absent, null, or blank (historical conversations, or a tool
 * the manifest hasn't labelled yet). A trailing defensive strip means the
 * "never an underscore" guarantee holds for the whole resolver, not just
 * the fallback branch — an authored label should never contain one, but a
 * manifest typo must not resurrect the raw-identifier bug this exists to
 * close.
 */
const VISHING_TOOL_LABEL_KEYS: Record<string, string> = {
  vishing_report_overview: 'vishing.ai.labels.reportOverview', vishing_report_departments: 'vishing.ai.labels.reportDepartments', vishing_report_employees: 'vishing.ai.labels.reportEmployees', vishing_report_employee_history: 'vishing.ai.labels.reportHistory', vishing_report_performance: 'vishing.ai.labels.reportPerformance', vishing_report_activity: 'vishing.ai.labels.reportActivity', vishing_report_skipped: 'vishing.ai.labels.reportSkipped',
  vishing_campaign_list: 'vishing.ai.labels.campaignList', vishing_campaign_get: 'vishing.ai.labels.campaignGet', vishing_campaign_create: 'vishing.ai.labels.campaignCreate', vishing_campaign_update: 'vishing.ai.labels.campaignUpdate', vishing_campaign_delete: 'vishing.ai.labels.campaignDelete',
  vishing_campaign_launch: 'vishing.ai.labels.campaignLaunch', vishing_campaign_pause: 'vishing.ai.labels.campaignPause', vishing_campaign_resume: 'vishing.ai.labels.campaignResume', vishing_campaign_terminate: 'vishing.ai.labels.campaignTerminate', vishing_campaign_audience_preview: 'vishing.ai.labels.audiencePreview', vishing_campaign_attempt_list: 'vishing.ai.labels.attemptList', vishing_campaign_target_list: 'vishing.ai.labels.targetList',
  vishing_voice_list: 'vishing.ai.labels.voiceList', vishing_voice_get: 'vishing.ai.labels.voiceGet', vishing_voice_create: 'vishing.ai.labels.voiceCreate', vishing_voice_update: 'vishing.ai.labels.voiceUpdate', vishing_voice_delete: 'vishing.ai.labels.voiceDelete', vishing_voice_clone: 'vishing.ai.labels.voiceClone',
  vishing_scenario_list: 'vishing.ai.labels.scenarioList', vishing_scenario_get: 'vishing.ai.labels.scenarioGet', vishing_scenario_create: 'vishing.ai.labels.scenarioCreate', vishing_scenario_update: 'vishing.ai.labels.scenarioUpdate', vishing_scenario_delete: 'vishing.ai.labels.scenarioDelete', vishing_scenario_clone: 'vishing.ai.labels.scenarioClone',
  vishing_library_capabilities_get: 'vishing.ai.labels.capabilities', vishing_customer_domain_list: 'vishing.ai.labels.domains', vishing_recipient_list: 'vishing.ai.labels.recipients', vishing_recipient_group_list: 'vishing.ai.labels.groups', vishing_target_preview: 'vishing.ai.labels.targetPreview',
  vishing_report_summary: 'vishing.ai.labels.reportSummary', vishing_settings_get: 'vishing.ai.labels.settingsGet', vishing_settings_update: 'vishing.ai.labels.settingsUpdate', vishing_company_settings_update: 'vishing.ai.labels.companySettingsUpdate', vishing_transport_list: 'vishing.ai.labels.transportList', vishing_readiness_get: 'vishing.ai.labels.readiness', vishing_outbound_readiness_get: 'vishing.ai.labels.outboundReadiness', vishing_runtime_status_get: 'vishing.ai.labels.runtime',
};
export function resolveToolLabel(toolName: string, toolLabel?: string | null, t?: (key: string) => string): string {
  if (t && VISHING_TOOL_LABEL_KEYS[toolName]) return t(VISHING_TOOL_LABEL_KEYS[toolName]);
  const authored = toolLabel?.trim()
  const resolved = authored ? authored : humanizeToolName(toolName)
  return resolved.includes('_') ? resolved.replace(/_/g, ' ').replace(/\s+/g, ' ').trim() : resolved
}
export function vishingApprovalKey(toolName: string): string | undefined {
  const actions: Record<string, string> = { vishing_campaign_launch: 'vishing.ai.approval.launch', vishing_campaign_resume: 'vishing.ai.approval.resume', vishing_campaign_pause: 'vishing.ai.approval.pause', vishing_campaign_terminate: 'vishing.ai.approval.terminate', vishing_campaign_delete: 'vishing.ai.approval.deleteCampaign', vishing_voice_delete: 'vishing.ai.approval.deleteVoice', vishing_scenario_delete: 'vishing.ai.approval.deleteScenario', vishing_settings_update: 'vishing.ai.approval.settings', vishing_company_settings_update: 'vishing.ai.approval.companySettings' };
  return actions[toolName];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Vishing campaign actions whose approval must NAME the campaign.
 *
 * Each takes the campaign's uuid as `id`. The generic summary below shows an
 * opaque id shortened ("77777777…") when it is the only argument and drops it
 * altogether when anything else is present, so the approver of a launch saw
 * either eight hex characters or no campaign at all — and never how many
 * people would be called. The verification dialog reads the campaign for these
 * tools and shows its name and that number; `people` says which number.
 */
const VISHING_CAMPAIGN_ACTIONS: Record<string, 'launch' | 'resume' | null> = {
  vishing_campaign_launch: 'launch',
  vishing_campaign_resume: 'resume',
  vishing_campaign_pause: null,
  vishing_campaign_terminate: null,
  vishing_campaign_delete: null,
  vishing_campaign_update: null,
};
export interface VishingCampaignSubject {
  id: string
  /** The domain to read the campaign in, when the call names one. */
  domainId: string | null
  /** 'launch': the people a launch would call. 'resume': the people still to be called. null: no count applies. */
  people: 'launch' | 'resume' | null
}
/** The campaign a Vishing campaign action is about; null for any other tool or an input without a campaign uuid. */
export function vishingCampaignSubject(toolName: string, input: Record<string, unknown>): VishingCampaignSubject | null {
  if (!Object.prototype.hasOwnProperty.call(VISHING_CAMPAIGN_ACTIONS, toolName)) return null
  const id = input.id
  if (typeof id !== 'string' || !UUID_RE.test(id)) return null
  const domainId = typeof input.domainId === 'string' && UUID_RE.test(input.domainId) ? input.domainId : null
  return { id, domainId, people: VISHING_CAMPAIGN_ACTIONS[toolName] }
}

/**
 * #932 — tag actions whose approval must NAME the tag and the people.
 *
 * Each carries only ids. The summary below drops a tag's uuid when the people
 * are present and turns the people into a count, so the approver of "add
 * QA-TEST-AI-1008 to one person" saw "Recipient IDs: 1 recipient ids", no tag
 * and no person. The verification dialog reads the tags and the people for
 * these tools and shows their names. `action` says what happens to the
 * person's tags: recipient_add_tags REPLACES them all with `tagIds`.
 *
 * The input is read as the API's handlers read it (recipients
 * ai-chat-tools/handlers.ts, tags.service.ts, resolveTargetIds), or the box
 * names a tag or people the call does not act on: the single-tag tools take
 * the tag from `tagId` before `id`; `selectAll` is any truthy value, and adds
 * everyone who matches `filters` (no filter: the whole workspace) to the
 * people in `ids`; `excludeIds` is taken out of both, last; an id counts
 * once, whatever its letter case. `shownKeys` are the input keys the subject
 * accounts for.
 */
export interface TagApprovalSubject {
  action: 'add' | 'remove' | 'replace'
  tagIds: string[]
  /** The people named by id, less excludeIds. With allMatching the API adds them to everyone who matches. */
  recipientIds: string[]
  /** Every recipient that matches the call's filters (selectAll). */
  allMatching: boolean
  /** With allMatching, the filters the API is sure to apply; [] = none, so the widest group. */
  filters: SelectionFilter[]
  /**
   * What the call sends that the API may not apply: a filter the box cannot
   * vouch for, filters sent without a select-all, an excludeIds a single-tag
   * tool does not read. Listed apart; it never narrows the group shown.
   */
  mayBeIgnored: SelectionFilter[]
  /** With allMatching, the people left out of everyone who matches (excludeIds). */
  excludedIds: string[]
  shownKeys: string[]
}
export interface SelectionFilter {
  key: string
  label: string
  values: string[]
}
const isUuidText = (item: unknown): item is string => typeof item === 'string' && UUID_RE.test(item)
/** The uuids in a list, each once whatever its letter case (as the API counts them). */
function uuidsIn(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  return value.filter((item): item is string => {
    if (!isUuidText(item) || seen.has(item.toLowerCase())) return false
    seen.add(item.toLowerCase())
    return true
  })
}
/**
 * The select-all filters the box vouches for, read as GET /v1/recipients'
 * FilterRecipientsDto and its list read them: the facets
 * (@Transform(toFacetArray): a value or a list, blanks dropped, the "every
 * option" sentinel anywhere = no filter) and search (plain text, applied when
 * not empty). Every other key is only listed as "may be ignored by the
 * server", because the box cannot be sure the API applies it: a filter whose
 * own transform drops an odd shape (domainId sent as a list), one that needs
 * a partner (cfDateKey without its dates), a name the validation pipe drops
 * (constructor). Shown as applied, each made the box name a narrow group
 * while the API acted on everyone (#932, tab 1's review).
 * select-all-filters-mirror-the-api-932.spec.ts reads the DTO and fails when
 * this list and its facets part ways.
 */
export const SELECT_ALL_FACETS = [
  'status',
  'departments',
  'designations',
  'locations',
  'countries',
  'tagIds',
  'groupIds',
  'employmentTypes',
  'isRemote',
  'completeness',
  'sources',
  'ids',
] as const
const SELECT_ALL_OPTIONS = '__ALL__'
const isBlankText = (item: unknown) => typeof item === 'string' && item.trim() === ''
/**
 * What a facet narrows to, in the list's own terms: [] = no filter; null =
 * the API refuses it (a member that is not text fails @IsString each).
 */
function facetValues(key: string, value: unknown): string[] | null {
  const members = (Array.isArray(value) ? value : [value]).filter((item) => !isBlankText(item))
  if (members.includes(SELECT_ALL_OPTIONS)) return []
  if (!members.every((item): item is string => typeof item === 'string')) return null
  // the list keeps a person whose flag EQUALS `value === 'true'`
  if (key === 'isRemote') return [...new Set(members.map((item) => (item === 'true' ? 'Yes' : 'No')))]
  // the list title-cases each status ('active' is Active)
  if (key === 'status') return members.map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
  return members.map(shortenId)
}
/** A value as sent: each text listed, ids shortened; anything else in words. null = nothing sent. */
function sentEntry(key: string, value: unknown): SelectionFilter | null {
  if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) return null
  const values =
    typeof value === 'string' && value.trim()
      ? [shortenId(value)]
      : Array.isArray(value) && value.every((item) => typeof item === 'string' && item.trim())
        ? value.map((item: string) => shortenId(item))
        : [describeArgValue(value, key)]
  return { key, label: argKeyLabel(key), values }
}
/**
 * A call's filters, split into those the API is sure to apply (only with a
 * select-all: without one the filters choose nobody) and those it may not.
 * null when `filters` is not an object, which the API refuses.
 */
function selectionFilters(filters: unknown, allMatching: boolean): { applied: SelectionFilter[]; mayBeIgnored: SelectionFilter[] } | null {
  if (filters === undefined || filters === null) return { applied: [], mayBeIgnored: [] }
  if (typeof filters !== 'object' || Array.isArray(filters)) return null
  const applied: SelectionFilter[] = []
  const mayBeIgnored: SelectionFilter[] = []
  for (const [key, value] of Object.entries(filters as Record<string, unknown>)) {
    if (value === undefined || value === null) continue
    if (allMatching && (SELECT_ALL_FACETS as readonly string[]).includes(key)) {
      const values = facetValues(key, value)
      if (values && values.length > 0) applied.push({ key, label: argKeyLabel(key), values })
      if (values) continue
    }
    if (allMatching && key === 'search' && typeof value === 'string') {
      // `if (filters.search)`: any text but '' is searched for, "__ALL__" and spaces included
      if (value !== '') applied.push({ key, label: argKeyLabel(key), values: [`"${truncate(value, 40)}"`] })
      continue
    }
    const sent = sentEntry(key, value)
    if (sent) mayBeIgnored.push(sent)
  }
  return { applied, mayBeIgnored }
}
/** What a tag action is about; null for any other tool, or an input the API refuses before it acts. */
export function tagApprovalSubject(toolName: string, input: Record<string, unknown>): TagApprovalSubject | null {
  switch (toolName) {
    case 'tag_assign':
    case 'tag_unassign': {
      const tagIds = uuidsIn(input.tagIds)
      // excludeIds goes last, out of the people named and out of everyone
      // who matches (resolveTargetIds), whatever the letter case
      const excludedIds = uuidsIn(input.excludeIds)
      const excluded = new Set(excludedIds.map((id) => id.toLowerCase()))
      const recipientIds = uuidsIn(input.ids).filter((id) => !excluded.has(id.toLowerCase()))
      const allMatching = Boolean(input.selectAll)
      const filters = selectionFilters(input.filters, allMatching)
      // no people and no select-all: the call changes nobody
      if (tagIds.length === 0 || !filters || (!allMatching && recipientIds.length === 0)) return null
      return {
        action: toolName === 'tag_assign' ? 'add' : 'remove',
        tagIds,
        recipientIds,
        allMatching,
        filters: filters.applied,
        mayBeIgnored: filters.mayBeIgnored,
        excludedIds: allMatching ? excludedIds : [],
        shownKeys: ['tagIds', 'ids', 'excludeIds', 'selectAll', 'filters'],
      }
    }
    case 'tag_assign_single':
    case 'tag_unassign_single': {
      // `input.tagId ?? input.id`, as handleTagAssignSingle reads it
      const tagKey = input.tagId !== undefined && input.tagId !== null ? 'tagId' : 'id'
      const tagId = input[tagKey]
      const recipientIds = uuidsIn(input.recipientIds)
      if (typeof tagId !== 'string' || !UUID_RE.test(tagId) || recipientIds.length === 0) return null
      // the single-tag routes read no excludeIds: it takes nobody out
      const ignoredExcludes = sentEntry('excludeIds', input.excludeIds)
      return {
        action: toolName === 'tag_assign_single' ? 'add' : 'remove',
        tagIds: [tagId],
        recipientIds,
        allMatching: false,
        filters: [],
        mayBeIgnored: ignoredExcludes ? [ignoredExcludes] : [],
        excludedIds: [],
        shownKeys: [tagKey, 'recipientIds', 'excludeIds'],
      }
    }
    case 'recipient_add_tags':
      // the DTO takes a list of uuids only (IsUUID each), or refuses the call
      if (!isUuidText(input.id) || !Array.isArray(input.tagIds) || !input.tagIds.every(isUuidText)) return null
      return {
        action: 'replace',
        tagIds: uuidsIn(input.tagIds),
        recipientIds: [input.id],
        allMatching: false,
        filters: [],
        mayBeIgnored: [],
        excludedIds: [],
        shownKeys: ['id', 'tagIds'],
      }
    default:
      return null
  }
}

// ── Approved-argument summarisation ─────────────────────────────────────────

/**
 * Tenant-scoping fields present on nearly every tool call. Never the
 * subject of the action from the approver's point of view — always hidden,
 * regardless of value shape. The last three are the other workspace keys the
 * tool executor reads (ai-tool-executor.service.ts, M-24): the header the
 * manifests expose, and two spellings models use (#932).
 */
const ALWAYS_HIDDEN_KEYS = new Set(['workspaceid', 'companyid', 'domainid', 'x-workspace-id', 'workspace_id', 'workspaceuuid'])

function isOpaqueIdField(key: string, value: unknown): boolean {
  return /id$/i.test(key) && typeof value === 'string' && UUID_RE.test(value)
}

/**
 * Curated labels for argument keys common across many tools, where the
 * generic conversion is technically accurate but reads like a field name
 * rather than something a human wrote ("cardCount" -> "Card Count" vs.
 * "Cards"). Keep this list small — everything else falls back to
 * `humanizeArgKey`.
 */
const ARG_KEY_LABELS: Record<string, string> = {
  cardCount: 'Cards',
  topic: 'Topic',
  title: 'Title',
  name: 'Name',
  subject: 'Subject line',
  message: 'Message',
  description: 'Description',
  reason: 'Reason',
  locale: 'Language',
  locales: 'Languages',
  tags: 'Tags',
  status: 'Status',
  query: 'Search',
  email: 'Email address',
}

function humanizeArgKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase())
  // "Deck Id" reads like a typo — a trailing Id/Ids word names an
  // identifier, not a word, so present it as the acronym.
  return titled.replace(/\bId\b/g, 'ID').replace(/\bIds\b/g, 'IDs')
}

function argKeyLabel(key: string): string {
  // own keys only: a key such as "__proto__" in the input (JSON.parse makes
  // it an own property) read Object.prototype, an object, as its label (#932)
  return Object.prototype.hasOwnProperty.call(ARG_KEY_LABELS, key) ? ARG_KEY_LABELS[key] : humanizeArgKey(key)
}

/** Lowercase, spaced form of a key, used only to build a plural noun for array counts. */
function spacedLowerKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
}

function truncate(value: string, max = 160): string {
  return value.length > max ? `${value.slice(0, max)}…` : value
}

/** Shortens an opaque uuid to a readable preview rather than dumping all 36 characters. */
export function shortenId(value: string): string {
  return UUID_RE.test(value) ? `${value.slice(0, 8)}…` : truncate(value, 40)
}

/** Renders one argument value in words. Never JSON — arrays/objects are summarised by count. */
function describeArgValue(value: unknown, key: string): string {
  if (value === null || value === undefined) return 'Not set'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.trim() ? truncate(value) : '(empty)'
  if (Array.isArray(value)) {
    const noun = spacedLowerKey(key)
    return `${value.length} ${noun.endsWith('s') ? noun : 'items'}`
  }
  if (typeof value === 'object') {
    const count = Object.keys(value as Record<string, unknown>).length
    return `${count} field${count === 1 ? '' : 's'}`
  }
  return truncate(String(value))
}

export interface ApprovedArgument {
  key: string
  label: string
  value: string
}

export interface ApprovedInputResult {
  /** What to render. Empty when there is nothing safe/meaningful to show. */
  args: ApprovedArgument[]
  /**
   * True only when `args` is empty BECAUSE every field was hidden as
   * tenant plumbing (not because the input itself was empty) — the caller
   * must render a fallback sentence in that case, never a blank box, or
   * the human loses the one signal that something is still being approved.
   */
  allHidden: boolean
}

/**
 * Reduces a raw tool-call input object to what a human should be asked to
 * approve.
 *
 * - `workspaceId` / `companyId` / `domainId` are always dropped — tenant
 *   scoping, never the subject of the action.
 * - An opaque UUID-valued `*Id` field is dropped IF something else in the
 *   input already names the subject (e.g. `topic` alongside `deckId`).
 * - If the id is the ONLY argument left, it is kept (shortened) instead of
 *   leaving the approver with nothing to look at — the token binds to
 *   exactly this input, so hiding its only content would ask for a blind
 *   approval.
 * - Every value renders as words; arrays/objects are summarised by count,
 *   never JSON-dumped.
 */
export function summarizeApprovedInput(input: Record<string, unknown>): ApprovedInputResult {
  const originalKeyCount = Object.keys(input).length
  const withoutPlumbing = Object.entries(input).filter(
    ([key]) => !ALWAYS_HIDDEN_KEYS.has(key.toLowerCase()),
  )

  if (withoutPlumbing.length === 0) {
    return { args: [], allHidden: originalKeyCount > 0 }
  }

  const meaningful = withoutPlumbing.filter(([key, value]) => !isOpaqueIdField(key, value))
  const idOnly = withoutPlumbing.filter(([key, value]) => isOpaqueIdField(key, value))

  if (meaningful.length > 0) {
    return {
      args: meaningful.map(([key, value]) => ({
        key,
        label: argKeyLabel(key),
        value: describeArgValue(value, key),
      })),
      allHidden: false,
    }
  }

  // Every remaining argument is an opaque id — it IS the subject, so show
  // it (shortened) rather than nothing.
  return {
    args: idOnly.map(([key, value]) => ({
      key,
      label: argKeyLabel(key),
      value: shortenId(value as string),
    })),
    allHidden: false,
  }
}
